import { useEffect, useRef, useState } from "react";
import {
  Datagrid,
  FunctionField,
  List,
  NumberField,
  ReferenceInput,
  type RowClickFunction,
  SelectInput,
  useListContext,
} from "react-admin";
import { InstructorIcon } from "../../icons/InstructorIcon";
import { DateField } from "../../fields/DateField";
import { DateTime } from "luxon";
import { Course } from "../../providers/coursesProvider";
import { Row } from "../../misc/Row";
import { autovioColors } from "../../misc/backofficeTheme";
import { Box, Button, IconButton, Typography, useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import { useDialog } from "../../hooks/useDialog";
import { CourseDetailsDialog } from "../../dialogs/CourseDetailsDialog";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import IconInACircle from "../../misc/IconInACircle";
import AddIcon from "@mui/icons-material/Add";
import { grants } from "../../backoffice.access_control";
import { CreateCourseDialog } from "../../dialogs/CreateCourseDialog";
import { LocationField } from "../../fields/LocationField";
import { EmptyState } from "../../misc/EmptyState";
import { useCurrentDrivingSchoolId } from "../../hooks/useCurrentDrivingSchoolId";
import { useCurrentDrivingSchool } from "../../hooks/useCurrentDrivingSchool";
import { Column } from "../../misc/Column";
import { CourseMobileListItem } from "./CourseMobileListItem";
import { ResponsiveListSurface } from "../../misc/ResponsiveListSurface";

export function DrivingSchoolCoursesList() {
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const drivingSchool = useCurrentDrivingSchool();
  const isCompact = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const now = DateTime.now();
  const [selectedCourse, setSelectedCourse_] = useState<undefined | Course>();
  const selectedCourseChangedRef = useRef(false);
  const setSelectedCourse = (course?: Course) => {
    setSelectedCourse_(course);
    selectedCourseChangedRef.current = true;
    // Side effect: update browser URL ...
    const url = new URL(location.href);
    if (course) {
      if (url.searchParams.get("openDialog") !== `CourseDetailsDialog!${course.id}`) {
        url.searchParams.set("openDialog", `CourseDetailsDialog!${course.id}`);
        browserHistory.replace(url.toString());
      }
    } else {
      url.searchParams.delete("openDialog");
      browserHistory.replace(url.toString());
    }
  };
  useEffect(() => {
    const openDialog = new URL(location.href).searchParams.get("openDialog");
    if (openDialog) {
      void (async () => {
        const match = /^CourseDetailsDialog!([0-9a-f-]+)$/.exec(openDialog);
        if (match) {
          const id = match[1];
          const { data: course } = await dataProvider.getOne<Course>("courses", { id, meta: { drivingSchoolId } });
          if (!selectedCourseChangedRef.current) {
            setSelectedCourse_(course);
            return;
          }
        }
      })();
    }
  }, [drivingSchoolId]);
  const canCreateCourse =
    grants.has("theoryCoursesCreate") || grants.has("TheoryCourse:create") || grants.has("Course:create");
  const hasBranchFilter = (drivingSchool?.branches.length ?? 0) > 1;
  const { dialogProps: newCourseDialogProps, openDialog: openNewCourseDialog } = useDialog("NewCourseDialog");
  return (
    <ResponsiveListSurface variant="compactTabContent" data-testid="DrivingSchoolCoursesList">
      <List
        title=" " // <-- prevent that the default list title is rendered
        resource="courses"
        filter={{
          drivingSchoolId,
          end: (end: DateTime) => end > now,
        }}
        storeKey={`DrivingSchoolCoursesList@${drivingSchoolId}`}
        filters={
          hasBranchFilter ? [<_BranchFilter source="branchId" drivingSchoolId={drivingSchoolId} alwaysOn />] : []
        }
        actions={
          canCreateCourse &&
          (isCompact ? (
            <IconButton
              size="small"
              onClick={openNewCourseDialog}
              aria-label="Kurs hinzufügen"
              sx={{ ml: "auto !important", mr: 0.5 }}
            >
              <AddIcon />
            </IconButton>
          ) : (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openNewCourseDialog}
              size="small"
              sx={{ ml: "auto !important" }}
            >
              {"Hinzufügen\u00A0"}
            </Button>
          ))
        }
        sort={/* initial sort order: */ { field: "start", order: "ASC" }}
        exporter={false}
        empty={<EmptyState noEntriesText="Noch kein Kurs hinterlegt." />}
        sx={{
          pt: "20px",
          boxShadow: "none",
          "& .RaList-main": { overflow: "visible" },
          "& .RaList-main > .RaList-actions": {
            width: "100% !important",
          },
          "& .RaList-content": {
            boxShadow: "none",
            border: "none",
            borderRadius: 0,
            overflowX: "auto",
          },
          "& .RaDatagrid-root": {
            boxShadow: "none !important",
            borderRadius: 0,
          },
        }}
      >
        <_Datagrid
          rowClick={(_, __, record) => {
            setSelectedCourse(record as Course);
            return false;
          }}
        />
      </List>
      <CreateCourseDialog {...newCourseDialogProps} />
      {selectedCourse && (
        <CourseDetailsDialog
          course={selectedCourse}
          open
          onClose={() => setSelectedCourse()}
          onCloned={setSelectedCourse}
        />
      )}
    </ResponsiveListSurface>
  );
}

function _BranchFilter(props: { source: string; drivingSchoolId: string; alwaysOn?: boolean }) {
  const { source, drivingSchoolId } = props;

  return (
    <ReferenceInput source={source} reference="branches" filter={{ drivingSchoolId }} alwaysOn>
      <SelectInput
        label="Filiale"
        emptyText="Alle Filialen"
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
        size="small"
      />
    </ReferenceInput>
  );
}

function _Datagrid(props: { rowClick: RowClickFunction }) {
  const { rowClick } = props;
  const { isLoading, data, total } = useListContext<Course>();
  const drivingSchool = useCurrentDrivingSchool();
  const isCompact = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const empty = (
    <EmptyState noEntriesText="Noch kein Kurs hinterlegt." noMatchingEntriesText="Keine passenden Kurse gefunden." />
  );

  if (isLoading) {
    return <LoadingIndicator sx={{ position: "relative", borderRadius: "8px" }} />;
  }

  if (isCompact) {
    if (!data || data.length === 0 || total === 0) {
      return empty;
    }

    return (
      <Column sx={{ gap: 1, py: "1px" }}>
        {data?.map((course) => (
          <CourseMobileListItem
            key={course.id}
            course={course}
            branchName={drivingSchool?.branchesById[course.branchId]?.name}
            onClick={() => {
              void rowClick(course.id, "courses", course);
            }}
          />
        ))}
      </Column>
    );
  }

  return (
    <Datagrid rowClick={rowClick} bulkActionButtons={false} className="courses-list" empty={empty}>
      <FunctionField label="Kursart" render={_renderIconAndName} />
      <LocationField label="Filiale" />
      <NumberField label="Termine" source="numEvents" textAlign="left" />
      <DateField label="Start" source="start" />
      <DateField label="Ende" source="end" />
      <FunctionField label="Teilnehmer" render={_renderAttendees} />
      <FunctionField label="Status" render={_renderStatus} />
    </Datagrid>
  );
}

function _renderAttendees(course: Course) {
  const { numAttendees, maxAttendees } = course;
  const text = typeof maxAttendees === "number" ? `${numAttendees}/${maxAttendees}` : numAttendees.toString();
  return <Typography variant="body2">{text}</Typography>;
}

function _renderStatus(course: Course) {
  const status = course.start < DateTime.now() ? "Aktiv" : "Geplant";
  return (
    <Row gap={1} alignItems="center">
      <Box
        sx={{
          width: "12.8px",
          height: "12.8px",
          border: "2.4px solid",
          borderColor: "divider",
          borderRadius: "6.4px",
          backgroundColor: status === "Aktiv" ? autovioColors.green : "background.paper",
        }}
      />
      <Typography component="div" variant="body2">
        {status}
      </Typography>
    </Row>
  );
}

function _renderIconAndName(course: Course) {
  return (
    <Box sx={{ display: "flex", gap: "15px", alignItems: "center", marginLeft: "16px" }}>
      <IconInACircle icon={<InstructorIcon />} />
      <Typography sx={{ fontSize: 14 }}>{course.name}</Typography>
    </Box>
  );
}
